import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateTempWorkerMutation, useUpdateTempWorkerMutation } from '../../../../hooks/mutations/useTempWorkerMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { TempWorker } from '../../../../services/apiSLTP/materials/tempWorkers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    tempWorker: TempWorker | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateTempWorkerModal: React.FC<ModalProps> = ({ tempWorker, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [designation, setDesignation] = useState(tempWorker?.designation || '');
    const [codeProC, setCodeProC] = useState(tempWorker?.codeProC || '');
    const [email, setEmail] = useState(tempWorker?.email || '');
    const [isActive, setIsActive] = useState(tempWorker?.crudStatus ? tempWorker?.crudStatus === crudStatusEnum.OK : true);
    const [displayOrder, setDisplayOrder] = useState(typeof tempWorker?.displayOrder === 'number' ? tempWorker?.displayOrder.toString() : '');

    const { mutate: mutateTempWorker } = useUpdateTempWorkerMutation({
        onSuccess: refetch,
        onError: (error) => {
            console.error('Error updating tempWorker:', error.message);
        },
    });

    const { mutate: addTempWorker } = useCreateTempWorkerMutation({
        onSuccess: refetch,
        onError: (error) => {
            console.error('Error adding tempWorker:', error.message);
        },
    });

    const isFormValid = () => designation.length && codeProC.length;

    const handleSubmit = () => {
        const tempWorkerData = {
            designation,
            crudStatus: isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE,
            codeProC,
            email,
            displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
        };

        if (isEditMode) {
            if (tempWorker?._id) {
                mutateTempWorker({
                    tempWorkerId: tempWorker._id,
                    data: tempWorkerData,
                });
            }
        } else {
            addTempWorker(tempWorkerData);
        }
    };

    return (
        <ModalBody
            title={t('tempWorker')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!isFormValid()}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={(text) => setDesignation(text)} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={(text) => setCodeProC(text)} required />
            <ToggleLine checked={isActive} onChange={(checked) => setIsActive(checked)} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={email} label={t('email')} onChange={(text) => setEmail(text)} />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateTempWorkerModal;
